import { EventDispatcher } from "three";
import { EDITOR_SOCKET_EVENTS } from "./Events";
import { NOTIFICATION_EVENTS } from "../constants/notifications";
import io from "socket.io-client";
import { SCENE_EVENTS } from "../components/MVC/Model";
import { handleRestriction } from "./socketHelper";

export default class RoomEditSocket extends EventDispatcher {
  constructor({ model }) {
    super();
    this.model = model;
    this.isConnected = false;
    this.isLocked = false;

    this.intervalTakeoverRequesting = null;
    this.intervalTakeoverRequest = null;
    this.intervalTakeoverDelay = 30;
    this.intervalLockEditor = null;
    this.intervalLockEditorDelay = 30 * 60; // 30 minutes
    this.intervalLockEditorDelayHint = 5 * 60; // 5 minutes

    this.socket = io(model.URLS["socketUrl"], {
      transports: ["websocket", "polling"],
      query: model.URLS,
    });

    $("#lockEditor-toggle").hide();

    this.socket.on("connect", () => {
      this.isConnected = true;
      $("#lockEditor-toggle").show();
    });

    this.socket.on('initialized', () => {
      this.socket.emit('client_ready');
      this.socket.emit(EDITOR_SOCKET_EVENTS.JOIN_EDITOR);
    })

    handleRestriction(this.socket, this.model, () => {
      this.forceDisconnect()
    });

    this.model.addEventListener(SCENE_EVENTS.SCENE_SAVED_SUCCESS, () => {
      this.socket.emit(EDITOR_SOCKET_EVENTS.LOCK_EDITOR, {
        isLocked: false,
      });
    });

    this.model.addEventListener(NOTIFICATION_EVENTS.OPENED, ({ data }) => {
      if (!data.id) {
        return;
      }
      switch (data.id) {
        case EDITOR_SOCKET_EVENTS.MODAL_TAKEOVER_REQUEST:
          break;
        case EDITOR_SOCKET_EVENTS.MODAL_CONFIRM_TAKEOVER_REQUEST_PENDING:
          let pendingDelay = this.intervalTakeoverDelay;
          this.intervalTakeoverRequesting = setInterval(() => {
            pendingDelay--;
            const $pendingTimer = $(".pending-countdown-timer");
            $pendingTimer.text(`(${pendingDelay}s)`);
            if (pendingDelay <= 0) {
              clearInterval(this.intervalTakeoverRequesting);
              $pendingTimer.remove();
            }
          }, 1000);
          break;
        case EDITOR_SOCKET_EVENTS.MODAL_CONFIRM_TAKEOVER_REQUEST:
          let delay = this.intervalTakeoverDelay;
          const $okButton = $(".confirm-ok");
          $okButton.append(
            `<span class="countdown-timer">(${this.intervalTakeoverDelay}s)</span>`
          );
          this.intervalTakeoverRequest = setInterval(() => {
            delay--;
            $okButton.find(".countdown-timer").text(`(${delay}s)`);
            if (delay <= 0) {
              clearInterval(this.intervalTakeoverRequest);
              this.acceptTakeoverRequest(data.payload.requester);
              $okButton.find(".countdown-timer").remove();
            }
          }, 1000);
          break;
        default:
          break;
      }
    });

    this.model.addEventListener(NOTIFICATION_EVENTS.CLOSED, ({ data }) => {
      if (!data.id) {
        return;
      }
      if (data.id === EDITOR_SOCKET_EVENTS.MODAL_CONFIRM_TAKEOVER_REQUEST) {
        clearInterval(this.intervalTakeoverRequest);
      }
      if (data.id === EDITOR_SOCKET_EVENTS.MODAL_CONFIRM_TAKEOVER_REQUEST_PENDING) {
        clearInterval(this.intervalTakeoverRequesting);
      }
    });

    this.lockEditor();
    this.autoUnlockEditor();

    this.socket.on(EDITOR_SOCKET_EVENTS.LOCK_STATUS, (data) => {
      const { isLocked } = data;
      this.isLocked = isLocked;
      $("#lockEditor").prop("checked", isLocked);
    });

    this.socket.on(EDITOR_SOCKET_EVENTS.EDITOR_STATUS, async (data) => {
      const { status, editor } = data;

      if (status === EDITOR_SOCKET_EVENTS.EDITOR_STATUS_LOCKED) {
        $("#lockEditor").prop("checked", true);
        $("#publish_changes, #preview_mode").addClass("disabled");

        await this.closeModal();

        this.model.dispatchEvent({
          type: NOTIFICATION_EVENTS.CONFIRM_WITHOUT_CLOSE,
          data: {
            title: $trans('session-locked-by-title'),
            header: true,
            message: $trans('session-locked-by-message', { userName: editor.userName }),
            successBtn: false,
            cancelBtnTxt: $trans('back-to-home'),
            onCancel: () => {
              this.closeEditor();
            },
          },
        });
        return;
      }

      if (status === EDITOR_SOCKET_EVENTS.EDITOR_STATUS_OCCUPIED) {
        $("#publish_changes, #preview_mode").addClass("disabled");

        await this.closeModal();

        this.model.dispatchEvent({
          type: NOTIFICATION_EVENTS.CONFIRM_WITHOUT_CLOSE,
          data: {
            id: EDITOR_SOCKET_EVENTS.MODAL_TAKEOVER_REQUEST,
            title: $trans('session-in-progress-title'),
            header: true,
            message: $trans('session-in-progress-message', { userName: editor.userName }),
            confirmBtnTxt: $trans('takeover-button'),
            success: () => {
              this.socket.emit(EDITOR_SOCKET_EVENTS.REQUEST_TAKEOVER);
            },
            cancelBtnTxt: $trans('back-to-home'),
            onCancel: () => {
              this.closeEditor();
            },
          },
        });
        return;
      }

      if (status === EDITOR_SOCKET_EVENTS.EDITOR_STATUS_OPEN) {
        return;
      }
    });

    /**
     * Takeover request from the editor
     */
    this.socket.on(EDITOR_SOCKET_EVENTS.TAKEOVER_REQUEST, async (data) => {
      const { requester } = data;

      await this.closeModal();

      this.model.dispatchEvent({
        type: NOTIFICATION_EVENTS.CONFIRM_WITHOUT_CLOSE,
        data: {
          id: EDITOR_SOCKET_EVENTS.MODAL_CONFIRM_TAKEOVER_REQUEST,
          title: $trans('takeover-request-title'),
          header: true,
          message: $trans('takeover-request-message', { userName: requester.userName }),
          confirmBtnTxt: $trans('approve-text'),
          success: () => {
            this.acceptTakeoverRequest(requester);
          },
          cancelBtnTxt: $trans('reject-text'),
          onCancel: () => {
            this.acceptTakeoverRequest(requester);
          },
          payload: {
            requester,
          },
        },
      });
    });

    /**
     * Takeover response from the editor
     */
    this.socket.on(EDITOR_SOCKET_EVENTS.TAKEOVER_RESPONSE, async (data) => {
      const { status, editor } = data;

      await this.closeModal();

      if (status === EDITOR_SOCKET_EVENTS.TAKEOVER_RESPONSE_PENDING) {
        this.model.dispatchEvent({
          type: NOTIFICATION_EVENTS.CONFIRM_WITHOUT_CLOSE,
          data: {
            id: EDITOR_SOCKET_EVENTS.MODAL_CONFIRM_TAKEOVER_REQUEST_PENDING,
            title: $trans('takeover-pending-title'),
            header: true,
            message: $trans('takeover-pending-message', {
              userName: editor.userName,
              countdown: '<span class="pending-countdown-timer">(30s)</span>',
            }),
            successBtn: false,
            cancelBtnTxt: $trans('back-to-home'),
            onCancel: () => {
              this.closeEditor();
            },
          },
        });
        return;
      }

      if (status === EDITOR_SOCKET_EVENTS.TAKEOVER_RESPONSE_ACCEPTED) {
        this.model.dispatchEvent({
          type: NOTIFICATION_EVENTS.CONFIRM_WITHOUT_CLOSE,
          data: {
            title: $trans('takeover-approved-title'),
            header: true,
            message: $trans('takeover-approved-message', {
              userName: editor.userName,
            }),
            cancelBtn: false,
            successBtnTxt: $trans('ok-text'),
            success: () => {
              this.closeModal();
            },
          },
        });
        return;
      }

      if (status === EDITOR_SOCKET_EVENTS.TAKEOVER_RESPONSE_REJECTED) {
        this.model.dispatchEvent({
          type: NOTIFICATION_EVENTS.CONFIRM_WITHOUT_CLOSE,
          data: {
            title: $trans('takeover-rejected-title'),
            header: true,
            message: $trans('takeover-rejected-message', {
              userName: editor.userName,
            }),
            successBtn: false,
            cancelBtnTxt: $trans('back-to-home'),
            onCancel: () => {
              this.closeEditor();
            },
          },
        });
        return;
      }
    });

    this.socket.on(
      EDITOR_SOCKET_EVENTS.CONFIRM_TAKEOVER_RESPONSE,
      async (data) => {
        const { status, requester, message } = data;

        if (status === EDITOR_SOCKET_EVENTS.TAKEOVER_RESPONSE_ACCEPTED) {
          await this.closeModal();

          this.model.dispatchEvent({
            type: NOTIFICATION_EVENTS.CONFIRM_WITHOUT_CLOSE,
            data: {
              title: $trans('takeover-approved-title'),
              header: true,
              message: $trans('takeover-approved-by-message', {
                userName: requester.userName,
              }),
              successBtn: false,
              onCancel: () => {
                this.closeEditor();
              },
              cancelBtnTxt: $trans('back-to-home'),
            },
          });
          return;
        }

        if (status === EDITOR_SOCKET_EVENTS.TAKEOVER_RESPONSE_CANCELLED) {
          await this.closeModal();

          this.model.dispatchEvent({
            type: NOTIFICATION_EVENTS.CONFIRM_WITHOUT_CLOSE,
            data: {
              title: $trans('takeover-cancelled-title'),
              header: true,
              message,
              cancelBtnTxt: $trans('ok-text'),
            },
          });
          return;
        }
      }
    );
  }

  acceptTakeoverRequest = (requester) => {
    this.socket.emit(EDITOR_SOCKET_EVENTS.CONFIRM_TAKEOVER, {
      status: EDITOR_SOCKET_EVENTS.TAKEOVER_RESPONSE_ACCEPTED,
      requester,
    });
  };

  rejectTakeoverRequest = (requester) => {
    this.socket.emit(EDITOR_SOCKET_EVENTS.CONFIRM_TAKEOVER, {
      status: EDITOR_SOCKET_EVENTS.TAKEOVER_RESPONSE_REJECTED,
      requester,
    });
  };

  closeEditor = () => {
    window.location.href = "/";
  };

  closeModal = () => {
    return new Promise((resolve) => {
      this.model.dispatchEvent({ type: NOTIFICATION_EVENTS.CLOSE });
      setTimeout(() => {
        resolve();
      }, 100);
    });
  };

  lockEditor = () => {
    $("#lockEditor").on("change", (e) => {
      if (!this.isConnected) {
        return;
      }
      if (e.target.checked) {
        this.socket.emit(EDITOR_SOCKET_EVENTS.LOCK_EDITOR, {
          isLocked: true,
        });
        this.model.dispatchEvent({
          type: NOTIFICATION_EVENTS.CONFIRM_WITHOUT_CLOSE,
          data: {
            title: $trans('session-locked-title'),
            header: true,
            message: $trans('session-locked-message'),
            successBtn: false,
            cancelBtnTxt: $trans('ok-text'),
          },
        });
      } else {
        this.socket.emit(EDITOR_SOCKET_EVENTS.LOCK_EDITOR, {
          isLocked: false,
        });
        this.model.dispatchEvent({
          type: NOTIFICATION_EVENTS.CONFIRM_WITHOUT_CLOSE,
          data: {
            title: $trans('session-unlocked-title'),
            header: true,
            message: $trans('session-unlocked-message'),
            successBtn: false,
            cancelBtnTxt: $trans('ok-text'),
          },
        });
      }
    });
  };

  autoUnlockEditor = () => {
    $(document).on("mousemove keydown touchstart", () => {
      if (this.intervalLockEditor) {
        clearInterval(this.intervalLockEditor);
        $("#lockEditor-toggle").find(".countdown-timer").remove();
      }

      if (!this.isLocked) {
        return;
      }

      let delay = this.intervalLockEditorDelay;
      this.intervalLockEditor = setInterval(() => {
        delay--;

        if (delay === this.intervalLockEditorDelayHint) {
          const hint = this.getCountdownText(this.intervalLockEditorDelayHint);
          $("#lockEditor-toggle .lockEditor-label").after(
            `<span class="countdown-timer">(${hint})</span>`
          );
        }

        if (delay <= this.intervalLockEditorDelayHint) {
          const timer = this.getCountdownText(delay);
          $("#lockEditor-toggle").find(".countdown-timer").text(`(${timer})`);
        }

        if (delay <= 0) {
          this.socket.emit(EDITOR_SOCKET_EVENTS.LOCK_EDITOR, {
            isLocked: false,
          });
          clearInterval(this.intervalLockEditor);
          $("#lockEditor-toggle").find(".countdown-timer").remove();
        }
      }, 1000);
    });
  };

  getCountdownText = (delay) => {
    return `${delay}s`;
  };
  enviromentChanged = (id, userId) => {
    this.socket.emit("message", {
      eventName: 'environmentChanged',
      senderId: userId,
      roomId: id
    });
  }

  disconnect = async () => {
    if (this.socket?.connected) {
      await this.socket.disconnect();
    }
  }

  forceDisconnect = async () => {
    this.forcedReload = true;
    await this.disconnect();
    this.socket?.removeAllListeners?.();
  }
}
